import { GeneralLedger } from "types/Models/books";

export type TrialBalanceRow = GeneralLedger & {
  monthData?: Record<string, GeneralLedger>;
};

const getCategoryKey = (cat: GeneralLedger) => cat.uuid;

const attachMonthData = (
  category: GeneralLedger,
  month: string,
  allNodes: Map<string, TrialBalanceRow>
) => {
  const node = allNodes.get(category.uuid);
  if (node) {
    node.monthData![month] = category;
  }

  category.types?.forEach((child) => attachMonthData(child, month, allNodes));
};

export const buildTrialBalanceData = (
  data: GeneralLedger[] | Record<string, GeneralLedger[]>,
  isGroupedByMonth: boolean
): TrialBalanceRow[] => {
  if (!isGroupedByMonth) {
    return data as GeneralLedger[];
  }

  if (Array.isArray(data)) return [];

  const monthlyData = data as Record<string, GeneralLedger[]>;
  const months = Object.keys(monthlyData);
  if (months.length === 0) return [];

  const allNodes = new Map<string, TrialBalanceRow>();
  const parentMap = new Map<string, string | undefined>();

  // Pass 1: collect all categories from all months
  const walk = (category: GeneralLedger, parentUuid?: string) => {
    const key = getCategoryKey(category);

    let node = allNodes.get(key);
    if (!node) {
      node = {
        ...category,
        types: [],
        monthData: {},
      };
      allNodes.set(key, node);
      parentMap.set(key, parentUuid);
    }

    category.types?.forEach((child) => walk(child, category.uuid));
  };

  months.forEach((month) => {
    monthlyData[month]?.forEach((category) => walk(category));
  });

  // Pass 2: attach month-wise data
  months.forEach((month) => {
    monthlyData[month]?.forEach((category) => {
      attachMonthData(category, month, allNodes);
    });
  });

  // Pass 3: rebuild tree
  allNodes.forEach((node) => (node.types = []));

  allNodes.forEach((node, key) => {
    const parentKey = parentMap.get(key);
    if (parentKey && allNodes.has(parentKey)) {
      allNodes.get(parentKey)!.types.push(node);
    }
  });

  // Pass 4: collect roots
  const roots: TrialBalanceRow[] = [];
  allNodes.forEach((node, key) => {
    const parentKey = parentMap.get(key);
    if (!parentKey || !allNodes.has(parentKey)) {
      roots.push(node);
    }
  });

  return roots;
};
